import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Receipt, Calendar, DollarSign, CheckCircle, Clock, XCircle } from 'lucide-react';
import html2canvas from 'html2canvas';

const PaymentReceiptModal = ({ isOpen, onClose, payment }) => {
  const receiptRef = useRef();

  const handleDownload = async () => {
    if (!receiptRef.current) return;
    const canvas = await html2canvas(receiptRef.current, { backgroundColor: "#fff" });
    const link = document.createElement('a');
    link.download = `comprobante_pago_${payment.id}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  if (!isOpen || !payment) return null;

  // Icono de estado
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'overdue':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative overflow-hidden"
          initial={{ scale: 0.95, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 30 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          {/* Botón cerrar */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 z-10"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          {/* Botón descargar */}
          <button
            onClick={handleDownload}
            className="absolute top-4 left-4 p-2 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors duration-200 z-10 flex items-center gap-1"
            title="Descargar comprobante"
          >
            <Download className="w-5 h-5 text-blue-600" />
          </button>
          {/* Recibo visual */}
          <div ref={receiptRef} className="bg-white px-8 py-6 rounded-xl border border-gray-300 shadow relative">
            {/* Encabezado tipo recibo */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Receipt className="w-7 h-7 text-blue-600" />
                <span className="text-lg font-bold text-blue-700 tracking-wide">RECIBO DE PAGO</span>
              </div>
              <span className="text-xs text-gray-400">#{payment.id}</span>
            </div>
            <div className="border-b border-dashed border-gray-400 mb-4"></div>
            {/* Datos principales */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Socio:</span>
                <span className="text-gray-900">{payment.memberName}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Tipo de Membresía:</span>
                <span className="text-gray-900 capitalize">{payment.membershipType}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Método de Pago:</span>
                <span className="text-gray-900">{payment.paymentMethod}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Estado:</span>
                <span className="flex items-center gap-1 text-gray-900">
                  {getStatusIcon(payment.status)}
                  {payment.status === 'completed' ? 'Completado' : payment.status === 'pending' ? 'Pendiente' : 'Vencido'}
                </span>
              </div>
            </div>
            <div className="border-b border-dashed border-gray-400 my-4"></div>
            {/* Monto y fechas */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Monto:</span>
                <span className="flex items-center gap-1 text-gray-900 font-bold text-lg">
                  <DollarSign className="w-4 h-4" /> ${Number(payment.amount).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Fecha de Pago:</span>
                <span className="flex items-center gap-1 text-gray-900">
                  <Calendar className="w-4 h-4" />
                  {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('es-CL') : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Fecha de Vencimiento:</span>
                <span className="flex items-center gap-1 text-gray-900">
                  <Calendar className="w-4 h-4" />
                  {payment.dueDate ? new Date(payment.dueDate).toLocaleDateString('es-CL') : '-'}
                </span>
              </div>
            </div>
            <div className="border-b border-dashed border-gray-400 my-4"></div>
            {/* Pie de recibo */}
            <div className="flex flex-col items-center mt-2">
              <span className="text-xs text-gray-500">Emitido por Chungwha Club</span>
              <span className="text-xs text-gray-400">Este comprobante es válido como constancia de pago.</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentReceiptModal;